import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useActionHook, useModals, usePrompt, useQuickViewQueries } from "hooks";
import { mapKeys, pickBy } from "lodash";
import {
  useDealActions,
  useMeasureParametersSelector,
  useQuickViewQueriesSelect,
  useReportsActions,
  useSelectedBookmarkSelector
} from "store";
import {
  IGetQuickViewData,
  QuickViewMeasureData,
  QuickViewMeasureFilter,
  QuickViewQueries,
  QuickViewTableColumn,
  QuickViewTableData,
  QuickViewTableRow,
  SheetSettings
} from "store/types";
import { toastify } from "utils/tyostify";

import { AddInvestor } from "../components/AddInvestor/Add-investor";
import { EditTableCell } from "../components/EditTableCell/EditTableCell";

export const SMART_SHEET_ROWS_PER_PAGE = 50;

export interface RowToUpdate {
  dealId: number;
  investorId?: number;
  roundId?: number;
}

export const isSheetSettingsEqual = (a: SheetSettings, b: SheetSettings): boolean =>
  a.currency === b.currency &&
  a.exchangeDate === b.exchangeDate &&
  a.dateRange?.to === b.dateRange?.to &&
  a.use_accounting_date === b.use_accounting_date;

export const DEFAULT_SHEET_SETTINGS: SheetSettings = {
  use_accounting_date: false,
  exchangeDate: undefined,
  dateRange: undefined,
  currency: undefined
};

export const SMART_SHEET_SETTINGS_KEYS = ["dateRange", "use_accounting_date", "currency", "exchangeDate"];

export interface SmartSheetContextValue {
  tableData: QuickViewTableData | null;
  loading: boolean;
  loadingMore: boolean;
  rowToUpdate: RowToUpdate | null;
  sheetSettings: SheetSettings;
  measureFilters: QuickViewMeasureFilter;
  containerRef: React.RefObject<HTMLDivElement>;
  updateSheetSettings: (settings: SheetSettings) => void;
  refetchTable: (overrides?: Partial<IGetQuickViewData>) => void;
  fetchNextPage: () => void;
  handleCellClick: (params: {
    row: QuickViewTableRow;
    column: QuickViewTableColumn;
    anchor: HTMLElement;
  }) => Promise<void>;
  handleFilterChange: (newFilters: QuickViewQueries["filters"]) => void;
  handleMeasureFilterChange: (newMeasureFilters: QuickViewMeasureFilter) => void;
  handleClearAll: () => void;
  resetSheetSettings: () => void;
  hasNonDefaultSettings: boolean;
  showEmptyState: boolean;
  deleteColumn: (item: QuickViewTableColumn) => void;
  tableFileLoading: boolean;
  exportTable: (groupByDeal: boolean) => void;
  recentlyUpdatedCells: Set<string>;
  clearRecentlyUpdatedCells: () => void;
}

export const SmartSheetContext = createContext<SmartSheetContextValue | null>(null);

interface SmartSheetProviderProps {
  children: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const DEFAULT_CONTAINER_REF = { current: null } as React.RefObject<HTMLDivElement>;

export const SmartSheetProvider: React.FC<SmartSheetProviderProps> = ({
  children,
  containerRef = DEFAULT_CONTAINER_REF
}) => {
  const {
    getQuickViewTableAction,
    getMeasureLatestValues,
    setQuickViewQueriesAction,
    getQuickViewSettingsAction,
    exportQuickViewTableAction
  } = useReportsActions();
  const { updateMeasuresValuesAction, updateDealAction, updateCharacteristicValueAction } = useDealActions();
  const { loading: tableFileLoading, action: exportTableFile } = useActionHook(exportQuickViewTableAction);
  const { filters, columns } = useQuickViewQueriesSelect();
  const { listen, isQueriesEmpty } = useQuickViewQueries();
  const measures = useMeasureParametersSelector();
  const selectedBookmark = useSelectedBookmarkSelector();
  const openPrompt = usePrompt();
  const { errorModal } = useModals();

  const [tableData, setTableData] = useState<QuickViewTableData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rowToUpdate, setRowToUpdate] = useState<RowToUpdate | null>(null);
  const [sheetSettings, setSheetSettings] = useState<SheetSettings>(DEFAULT_SHEET_SETTINGS);
  const [measureFilters, setMeasureFilters] = useState<QuickViewMeasureFilter>({});
  const [recentlyUpdatedCells, setRecentlyUpdatedCells] = useState<Set<string>>(new Set());
  const filtersPayload = useCallback(
    (f: QuickViewQueries["filters"]): IGetQuickViewData["filters"] =>
      pickBy(
        mapKeys(f, (_, key) => (key === "Deal Name" ? "deal_name" : key)),
        (value) => value.length
      ),
    []
  );

  const buildParams = useCallback(
    (overrides?: Partial<IGetQuickViewData>): IGetQuickViewData => ({
      page: 1,
      per_page: SMART_SHEET_ROWS_PER_PAGE,
      filters: filtersPayload(filters),
      group_by_deal: false,
      calculated_fields: columns.calculatedFields,
      characteristics: columns.characteristics,
      measure_ids: columns.measures,
      measure_filters: measureFilters,
      dateRange: sheetSettings.dateRange,
      currency: sheetSettings.currency,
      exchangeDate: sheetSettings.dateRange ? "toDate" : undefined,
      use_accounting_date: sheetSettings.use_accounting_date,
      ...overrides
    }),
    [filters, columns, measureFilters, sheetSettings, filtersPayload]
  );

  const lastParams = useRef<IGetQuickViewData>(buildParams());
  const isFetchingRef = useRef(false); //for blocking duplicate requests if scroll to quick

  const initSettingsAndLoadTable = useCallback(async () => {
    try {
      const res = await getQuickViewSettingsAction();

      const newColumns: QuickViewQueries["columns"] = {
        calculatedFields: res.data.filters.calculated_fields || [],
        characteristics: res.data.filters.characteristics || [],
        measures: res.data.filters.measure_ids || [],
        visibleSystemColumns: Array.isArray(res.data.filters.visible_system_columns)
          ? res.data.filters.visible_system_columns
          : columns.visibleSystemColumns,
        columnsOrder: columns.columnsOrder
      };

      setQuickViewQueriesAction({ queries: { columns: newColumns } });

      const params = buildParams({
        filters: {},
        calculated_fields: newColumns.calculatedFields,
        characteristics: newColumns.characteristics,
        measure_ids: newColumns.measures,
        measure_filters: {}
      });

      lastParams.current = params;

      const data = await getQuickViewTableAction(params);
      setTableData(data);
    } finally {
      setLoading(false);
    }
  }, [getQuickViewSettingsAction, getQuickViewTableAction, setQuickViewQueriesAction, buildParams]);

  // Always holds the latest initSettingsAndLoadTable without causing extra effect re-runs
  const initSettingsAndLoadTableRef = useRef(initSettingsAndLoadTable);
  initSettingsAndLoadTableRef.current = initSettingsAndLoadTable;

  const refetchTable = useCallback(
    (overrides?: Partial<IGetQuickViewData>) => {
      const newParams = buildParams({ ...overrides, page: 1 });

      lastParams.current = newParams;
      setLoading(true);
      setTableData((prev) => (prev ? { ...prev, rows: [], total: 0 } : null));

      return getQuickViewTableAction(newParams)
        .then((data) => setTableData(data))
        .finally(() => setLoading(false));
    },
    [buildParams, getQuickViewTableAction]
  );

  const updateSheetSettings = useCallback(
    (newSettings: SheetSettings) => {
      if (isSheetSettingsEqual(sheetSettings, newSettings)) return;

      setSheetSettings(newSettings);

      const tableParams: Partial<IGetQuickViewData> = {
        ...(newSettings.dateRange ? { dateRange: newSettings.dateRange } : {}),
        ...(newSettings.currency ? { currency: newSettings.currency } : {}),
        ...(newSettings.dateRange ? { exchangeDate: "toDate" as const } : {}),
        use_accounting_date: newSettings.use_accounting_date
      };

      refetchTable(tableParams);
    },
    [sheetSettings, refetchTable]
  );

  const resetSheetSettings = useCallback(() => {
    setSheetSettings(DEFAULT_SHEET_SETTINGS);
    refetchTable(DEFAULT_SHEET_SETTINGS);
  }, [refetchTable]);

  const handleFilterChange = useCallback(
    (newFilters: QuickViewQueries["filters"]) => {
      setQuickViewQueriesAction({ queries: { filters: newFilters } });
      refetchTable({ filters: filtersPayload(newFilters), page: 1 });
    },
    [setQuickViewQueriesAction, refetchTable, filtersPayload]
  );

  const handleMeasureFilterChange = useCallback(
    (newMeasureFilters: QuickViewMeasureFilter) => {
      setMeasureFilters(newMeasureFilters);
      refetchTable({ measure_filters: newMeasureFilters, page: 1 });
    },
    [refetchTable]
  );

  const hasNonDefaultSettings = useMemo(() => {
    return (SMART_SHEET_SETTINGS_KEYS as (keyof SheetSettings)[]).some(
      (key) => sheetSettings[key] !== DEFAULT_SHEET_SETTINGS[key]
    );
  }, [sheetSettings]);

  const handleClearAll = useCallback(() => {
    setMeasureFilters({});
    setQuickViewQueriesAction({ queries: { filters: {} } });
    refetchTable({ filters: {}, measure_filters: {}, page: 1 });
  }, [refetchTable, setQuickViewQueriesAction]);

  const fetchNextPage = useCallback(() => {
    if (isFetchingRef.current || loadingMore || loading) return;
    if (!tableData || !tableData.rows.length || tableData.rows.length >= tableData.total) return;

    setLoadingMore(true);
    isFetchingRef.current = true;

    const nextPage = Math.ceil(tableData.rows.length / SMART_SHEET_ROWS_PER_PAGE) + 1;

    getQuickViewTableAction({ ...lastParams.current, page: nextPage })
      .then((data) => {
        if (!data.rows.length) {
          setTableData((prev) => (prev ? { ...prev, total: prev.rows.length } : prev));
          return;
        }
        setTableData((prev) => {
          if (!prev) return data;
          return {
            ...data,
            rows: [...prev.rows, ...data.rows],
            total: data.total,
            columns: data.columns?.length ? data.columns : prev.columns
          };
        });
      })
      .finally(() => {
        setLoadingMore(false);
        isFetchingRef.current = false;
      });
  }, [loadingMore, loading, tableData, getQuickViewTableAction]);

  const deleteColumn = useCallback(
    (item: QuickViewTableColumn) => {
      if (!tableData) return;

      const measureId =
        item.type === "measure"
          ? item.key.startsWith("last_measure_")
            ? item.key.replace("last_measure_", "last")
            : item.key.split("_").pop()
          : undefined;

      setQuickViewQueriesAction({
        queries: {
          columns: {
            characteristics: columns.characteristics.filter((column) => column !== item.title),
            calculatedFields: columns.calculatedFields.filter((column) => column !== item.title),
            measures: item.type === "measure" ? columns.measures.filter((m) => m !== measureId) : columns.measures,
            columnsOrder: columns.columnsOrder.filter((key) => key !== item.key)
          }
        }
      });

      setTableData((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          columns: prev.columns.filter((column) => column.key !== item.key),
          rows: prev.rows.map((row) => {
            const newRow = { ...row };

            if (item.key in newRow) {
              delete newRow[item.key];
            }

            return newRow;
          })
        };
      });
    },
    [tableData, columns, setQuickViewQueriesAction]
  );

  const exportTable = useCallback(
    (groupByDeal: boolean) => {
      exportTableFile({
        group_by_deal: groupByDeal,
        filters: filtersPayload(filters),
        calculated_fields: columns.calculatedFields,
        characteristics: columns.characteristics,
        measure_ids: columns.measures,
        per_page: "infinite"
      });
    },
    [tableData, filters, columns, filtersPayload]
  );

  const markCellAsUpdated = useCallback((rowKey: string, columnKey: string) => {
    const cellKey = `${rowKey}:${columnKey}`;
    setRecentlyUpdatedCells((prev) => new Set(prev).add(cellKey));
  }, []);

  const clearRecentlyUpdatedCells = useCallback(() => {
    setRecentlyUpdatedCells(new Set());
  }, []);

  const updateRowInTable = useCallback(
    async (row: QuickViewTableRow, options?: { byDealOnly?: boolean; columnKey?: string }) => {
      if (!row.deal.id) {
        refetchTable();
        return;
      }

      if (options?.byDealOnly) {
        setRowToUpdate({ dealId: row.deal.id });

        const response = await getQuickViewTableAction({
          ...lastParams.current,
          page: 1,
          filters: { deal_id: [row.deal.id] }
        });

        setTableData((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            rows: prev.rows.map((r) => {
              if (r.deal.id !== row.deal.id) return r;

              const newRow = response.rows.find(
                (nr) => nr.deal.id === r.deal.id && nr.investor.id === r.investor.id && nr.round.id === r.round.id
              );

              return newRow || r;
            })
          };
        });

        if (options?.columnKey) {
          response.rows.forEach((r) => {
            markCellAsUpdated(`${r.deal.id}-${r.investor.id}-${r.round.id}`, options.columnKey!);
          });
        }
        setRowToUpdate(null);
        return;
      }

      if (!row.investor?.id || !row.round?.id) {
        refetchTable();
        return;
      }

      setRowToUpdate({ dealId: row.deal.id, investorId: row.investor.id, roundId: row.round.id });

      const response = await getQuickViewTableAction({
        ...lastParams.current,
        page: 1,
        filters: { deal_id: [row.deal.id], Investor: [row.investor.name!] }
      });

      const newRow = response.rows.find(
        (item) => item.deal.id === row.deal.id && item.investor.id === row.investor.id && item.round.id === row.round.id
      );

      if (!newRow) {
        console.error("Failed to find updated row");
        refetchTable();
        setRowToUpdate(null);
        return;
      }

      setTableData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) =>
            r.deal.id === row.deal.id && r.investor.id === row.investor.id && r.round.id === row.round.id ? newRow : r
          )
        };
      });
      if (options?.columnKey) {
        markCellAsUpdated(`${row.deal.id}-${row.investor.id}-${row.round.id}`, options.columnKey);
      }
      setRowToUpdate(null);
    },
    [getQuickViewTableAction, refetchTable, markCellAsUpdated]
  );

  const handleCellClick = useCallback(
    async ({ row, column, anchor }: { row: QuickViewTableRow; column: QuickViewTableColumn; anchor: HTMLElement }) => {
      if (column.key === "investor") {
        const result = await openPrompt({
          Component: AddInvestor,
          componentType: "popover",
          anchorEl: anchor,
          props: {
            dealData: {
              id: row.deal.id,
              name: row.deal.title
            },
            roundData: {
              id: row.round.id,
              name: row.round.name
            }
          }
        });

        if (result?.investorId) {
          try {
            await updateRowInTable(row, { columnKey: column.key });
            toastify({ message: "Investor added successfully", type: "success" });
          } catch (error) {
            errorModal({ msg: (error as Error).message });
          }
        }

        return;
      }

      const cell = row[column.key];

      if (!cell?.editable) return;

      const isNewValue = !cell?.value || cell.value === "-";

      const characteristicMessage = `Characteristic ${isNewValue ? "added" : "updated"} successfully`;
      const valueMessage = `Value ${isNewValue ? "added" : "updated"} successfully`;

      // Editable cell types:
      // cell.measure_id && cell.measure_data_id — last measure
      // cell.measure_id — sum measure data
      // cell.characteristic_id && cell.type — characteristic data ("number" | "text" | "percentage" | "currency" | "asset" | "date")
      // no fields — deal, investor, round, asset_class

      // Fetch measure history if needed (for both last measure and sum measure cases)
      let measureData: QuickViewMeasureData[] = [];
      if (column.type === "measure" && row.deal.id && cell.measure_id) {
        measureData = await getMeasureLatestValues({
          deal_id: row.deal.id,
          measure_id: cell.measure_id,
          round_id: row.round.id,
          investor_id: row.investor.id
        });
      }

      const measure = cell.measure_id ? measures.find((item) => item.id === cell.measure_id) : null;

      // Get data from form
      const result = await openPrompt({
        Component: EditTableCell,
        componentType: "popover",
        anchorEl: anchor,
        props: {
          columnKey: column.title,
          columnType: column.type as "measure" | "characteristic",
          isAbsolute: measure?.is_absolute ?? false,
          isNegative: measure?.is_negative ?? false,
          measureId: cell.measure_id,
          currency: cell.currency,
          measureData,
          value: cell.value,
          is_total: cell.is_total,
          dealId: row.deal.id,
          investorId: row.investor.id,
          roundId: row.round.id,
          ...(column.type === "characteristic" && { characteristicDataType: cell.type }),
          ...(cell.asset_class_id && { assetId: cell.asset_class_id }),
          ...(cell.sub_asset_class_id && { subAssetId: cell.sub_asset_class_id })
        }
      });

      const isEmptyMeasureUpdate =
        column.type === "measure" &&
        !result?.measureData.create.length &&
        !result?.measureData.update.length &&
        !result?.measureData.delete.length;

      if (!result || isEmptyMeasureUpdate) return;

      if (column.type === "characteristic" && !result.characteristicValue && isNewValue) {
        return;
      }

      // Update asset class
      if (column.type === "characteristic" && cell.type === "asset" && result.assetId && row.deal.id) {
        try {
          await updateDealAction({
            dealId: row.deal.id,
            ...(cell.hasOwnProperty("sub_asset_class_id")
              ? { sub_asset_class_id: result.assetId }
              : { asset_class_id: result.assetId })
          });
          await updateRowInTable(row, { byDealOnly: true, columnKey: column.key });
          toastify({ message: characteristicMessage, type: "success" });
        } catch (error) {
          errorModal({ msg: (error as Error).message });
        }
        return;
      }

      // Update characteristic value
      if (column.type === "characteristic" && cell.type !== "asset" && cell.characteristic_id && row.deal.id) {
        try {
          await updateCharacteristicValueAction({
            deal_id: row.deal.id,
            value: result.characteristicValue || "",
            id: cell.characteristic_id
          });
          await updateRowInTable(row, { byDealOnly: true, columnKey: column.key });
          toastify({ message: characteristicMessage, type: "success" });
          return;
        } catch (error) {
          errorModal({ msg: (error as Error).message });
        }
        return;
      }

      // Update measure data
      if (column.type === "measure" && cell.measure_id && row.deal.id) {
        try {
          await updateMeasuresValuesAction(result.measureData);
          await updateRowInTable(row, { columnKey: column.key });
          toastify({ message: valueMessage, type: "success" });
        } catch (error) {
          errorModal({ msg: (error as Error).message });
        }
        return;
      }
    },
    [
      measures,
      openPrompt,
      errorModal,
      getMeasureLatestValues,
      updateMeasuresValuesAction,
      updateDealAction,
      updateCharacteristicValueAction,
      updateRowInTable
    ]
  );

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters || {}).some((v) => v?.length > 0) || Object.keys(measureFilters || {}).length > 0;
  }, [filters, measureFilters]);

  const showEmptyState = !loading && !!tableData && tableData.rows.length === 0 && !hasActiveFilters;

  const isInitialMount = useRef(true);

  useEffect(() => {
    listen();
    isInitialMount.current = false;
    setLoading(true);

    if (isQueriesEmpty({ filters, columns, groupByDeal: false })) {
      initSettingsAndLoadTable();
    } else {
      refetchTable();
    }
  }, []);

  // When the user clicks a QuickView bookmark while already on this page,
  // React Router doesn't remount the component so the init effect never re-runs.
  // selectedBookmark changes only on bookmark click — not during initSettingsAndLoadTable —
  // so it's a safe signal to re-initialize without risking an infinite loop.
  useEffect(() => {
    if (isInitialMount.current) return;
    if (!selectedBookmark) return;

    setLoading(true);
    setMeasureFilters({});
    initSettingsAndLoadTableRef.current();
  }, [selectedBookmark]);

  const value = useMemo<SmartSheetContextValue>(
    () => ({
      tableData,
      loading,
      loadingMore,
      rowToUpdate,
      sheetSettings,
      measureFilters,
      containerRef,
      updateSheetSettings,
      refetchTable,
      fetchNextPage,
      handleCellClick,
      handleFilterChange,
      handleMeasureFilterChange,
      handleClearAll,
      resetSheetSettings,
      hasNonDefaultSettings,
      showEmptyState,
      deleteColumn,
      tableFileLoading,
      exportTable,
      recentlyUpdatedCells,
      clearRecentlyUpdatedCells
    }),
    [
      tableData,
      loading,
      loadingMore,
      rowToUpdate,
      sheetSettings,
      measureFilters,
      containerRef,
      updateSheetSettings,
      refetchTable,
      fetchNextPage,
      handleCellClick,
      handleFilterChange,
      handleMeasureFilterChange,
      handleClearAll,
      resetSheetSettings,
      hasNonDefaultSettings,
      showEmptyState,
      deleteColumn,
      tableFileLoading,
      exportTable,
      recentlyUpdatedCells,
      clearRecentlyUpdatedCells
    ]
  );

  return <SmartSheetContext.Provider value={value}>{children}</SmartSheetContext.Provider>;
};

export const useSmartSheet = (): SmartSheetContextValue => {
  const ctx = useContext(SmartSheetContext);
  if (ctx == null) {
    throw new Error("useSmartSheet must be used within SmartSheetProvider");
  }
  return ctx;
};
